import { VideoRenderExportType } from "@repo/api/video-render";
import { IconDownload, IconLink } from "@tabler/icons-react";
import vimeoLogo from "./icons/vimeo-logo.png";
import { ThirdPartyEditorInfoDto } from "@repo/api/third-party-video";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { ReactNode } from "react";

type Props = {
  type: VideoRenderExportType;
  onSelect: (type: VideoRenderExportType) => void;
};

type Option = {
  label: string;
  icon: ReactNode;
};

export function getOption(
  type: VideoRenderExportType,
  thirdPartyEditorInfo: ThirdPartyEditorInfoDto | null,
): Option {
  const defaultOptions: Record<VideoRenderExportType, Option> = {
    DOWNLOAD: {
      label: "Download video",
      icon: (
        <IconDownload
          className="h-11 w-11 text-primary"
          color="url(#icon-gradient)"
          stroke={1.5}
        />
      ),
    },
    LINK: {
      label: "Share link",
      icon: (
        <IconLink
          className="h-11 w-11 text-primary"
          color="url(#icon-gradient)"
          stroke={1.5}
        />
      ),
    },
    VIMEO: {
      label: "Upload to Vimeo",
      icon: <img src={vimeoLogo} className="h-11 w-11" />,
    },
  };

  if (
    type === "VIMEO" &&
    thirdPartyEditorInfo?.exportConfig.availableExports.VIMEO?.mode ===
      "TP_AUTO_SEND"
  ) {
    return {
      label: `Send to ${thirdPartyEditorInfo.displayName}`,
      icon: (
        <img src={thirdPartyEditorInfo.squareLogoSrc} className="h-11 w-11" />
      ),
    };
  }

  return defaultOptions[type];
}

export default function ExportOption({ type, onSelect }: Props) {
  const thirdPartyEditorInfo = useEditorStore(
    (state) => state.thirdPartyEditorInfo,
  );

  if (
    thirdPartyEditorInfo &&
    !thirdPartyEditorInfo.exportConfig.availableExports[type]
  ) {
    return null;
  }

  const option = getOption(type, thirdPartyEditorInfo);

  return (
    <button
      onClick={() => onSelect(type)}
      className="flex h-35 w-40 cursor-pointer flex-col items-center justify-center rounded-xl border border-grey-200 bg-white transition-colors duration-300 hover:bg-button-outline-hover"
    >
      {option.icon}
      <p className="mt-5 text-sm font-semibold">{option.label}</p>
    </button>
  );
}
